import React, { useState, useRef, useEffect } from 'react';
import {
    FileText,
    ArrowRight,
    CheckCircle2,
    Download,
    ScrollText,
    AlertTriangle,
    Mail,
    Lock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { jsPDF } from 'jspdf';

const TerminationOnboarding = ({ user, onComplete }) => {
    const [step, setStep] = useState(1);
    const [hasScrolled_bottom, setHasScrolledBottom] = useState(false);
    const [signature, setSignature] = useState('');
    const scrollRef = useRef(null);

    // Track timestamps for logging
    const [timestamps, setTimestamps] = useState({
        terminationStartedAt: user?.staffDetails?.contractData?.terminationStartedAt || new Date().toISOString(),
        terminationViewedAt: user?.staffDetails?.contractData?.terminationViewedAt || null
    });

    const personal = user?.staffDetails?.personalData || {};
    const business = user?.staffDetails?.businessData || {};
    const contract = user?.staffDetails?.contractData || {};

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop <= clientHeight + 50) {
            setHasScrolledBottom(true);
        }
    };

    const formatDate = (date) => {
        if (!date) return 'DATA_NÃO_ENCONTRADA';
        return new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
    };

    const generatePDF = () => {
        // Simple PDF generation for the user to keep a copy if they want
        const doc = new jsPDF();
        doc.setFontSize(12);
        doc.text("Termo de Quitação baixado para conferência.", 20, 20);
        doc.save("termo_quitacao.pdf");
    };

    return (
        <div className="flex items-center justify-center p-4">
            <div className="w-full max-w-4xl bg-[#09090b] border border-gray-800 rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row">

                {/* Sidebar / Progress */}
                <div className="w-full md:w-1/3 bg-gray-900/50 p-8 border-r border-gray-800 flex flex-col justify-between">
                    <div>
                        <div className="mb-8">
                            <ScrollText className="w-10 h-10 text-red-500 mb-4" />
                            <h2 className="text-xl font-bold text-white">Finalização de Contrato</h2>
                            <p className="text-gray-400 text-sm mt-2">Processo de encerramento e quitação.</p>
                        </div>

                        <div className="space-y-6 relative">
                            {/* Connector Line */}
                            <div className="absolute left-[19px] top-2 bottom-2 w-0.5 bg-gray-800 -z-10" />

                            <div className={`flex items-center gap-4 ${step >= 1 ? 'opacity-100' : 'opacity-40'}`}>
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${step > 1 ? 'bg-red-500 border-red-500' : step === 1 ? 'border-red-500 text-red-500 bg-red-500/10' : 'border-gray-700 bg-gray-800'}`}>
                                    {step > 1 ? <CheckCircle2 className="w-6 h-6 text-white" /> : <span className="font-bold">1</span>}
                                </div>
                                <div>
                                    <p className="text-white font-medium">Introdução</p>
                                    <p className="text-xs text-gray-500">O que é este termo</p>
                                </div>
                            </div>

                            <div className={`flex items-center gap-4 ${step >= 2 ? 'opacity-100' : 'opacity-40'}`}>
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${step > 2 ? 'bg-red-500 border-red-500' : step === 2 ? 'border-red-500 text-red-500 bg-red-500/10' : 'border-gray-700 bg-gray-800'}`}>
                                    {step > 2 ? <CheckCircle2 className="w-6 h-6 text-white" /> : <span className="font-bold">2</span>}
                                </div>
                                <div>
                                    <p className="text-white font-medium">Revisão do Termo</p>
                                    <p className="text-xs text-gray-500">Ler documento completo</p>
                                </div>
                            </div>

                            <div className={`flex items-center gap-4 ${step >= 3 ? 'opacity-100' : 'opacity-40'}`}>
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${step > 3 ? 'bg-red-500 border-red-500' : step === 3 ? 'border-red-500 text-red-500 bg-red-500/10' : 'border-gray-700 bg-gray-800'}`}>
                                    {step > 3 ? <CheckCircle2 className="w-6 h-6 text-white" /> : <span className="font-bold">3</span>}
                                </div>
                                <div>
                                    <p className="text-white font-medium">Confirmação</p>
                                    <p className="text-xs text-gray-500">Assinatura Digital</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 pt-6 border-t border-gray-800">
                        <div className="flex items-center gap-3">
                            {user?.avatar && (
                                <img
                                    src={`https://cdn.discordapp.com/avatars/${user.discordId}/${user.avatar}.png`}
                                    alt="Avatar"
                                    className="w-10 h-10 rounded-full grayscale"
                                />
                            )}
                            <div>
                                <p className="text-sm font-bold text-white">{user?.name}</p>
                                <p className="text-xs text-gray-400">Prestador</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content Area */}
                <div className="flex-1 p-8 md:p-12 relative flex flex-col">

                    {/* STEP 1: WELCOME */}
                    {step === 1 && (
                        <div className="flex-1 flex flex-col justify-center space-y-8 animate-in fade-in slide-in-from-right-8 duration-500">
                            <div className="w-16 h-16 bg-red-500/10 rounded-2xl flex items-center justify-center mb-4">
                                <AlertTriangle className="w-8 h-8 text-red-500" />
                            </div>

                            <div className="space-y-4">
                                <h1 className="text-3xl font-bold text-white">Fim do Ciclo</h1>
                                <p className="text-gray-300 text-lg leading-relaxed">
                                    Seu contrato está chegando ao fim. Para formalizar o encerramento da prestação de serviços,
                                    é necessário assinar o <strong>Termo de Quitação</strong>.
                                </p>
                                <p className="text-gray-400">
                                    Este documento certifica que todas as obrigações foram cumpridas e não há pendências de ambas as partes.
                                </p>
                            </div>



                            <Button
                                className="w-full max-w-sm bg-red-600 hover:bg-red-700 text-white py-6 text-lg shadow-lg shadow-red-500/20 mt-8"
                                onClick={() => {
                                    setStep(2);
                                    if (!timestamps.terminationViewedAt) {
                                        setTimestamps(prev => ({ ...prev, terminationViewedAt: new Date().toISOString() }));
                                    }
                                }}
                            >
                                Iniciar Leitura
                                <ArrowRight className="ml-2 w-5 h-5" />
                            </Button>
                        </div>
                    )}

                    {/* STEP 2: REVIEW */}
                    {step === 2 && (
                        <div className="space-y-6">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h2 className="text-2xl font-bold text-white">Termo de Quitação</h2>
                                    <p className="text-gray-400 text-sm">Leia atentamente para prosseguir.</p>
                                </div>
                            </div>

                            <ScrollArea
                                className="h-[400px] w-full rounded-md border border-gray-800 bg-gray-950 p-6 text-gray-300 shadow-inner"
                                onScrollCapture={handleScroll}
                            >
                                <div className="space-y-6 font-serif text-sm leading-relaxed select-none">
                                    <div className="text-center space-y-2 mb-8">
                                        <h3 className="font-bold text-white text-lg uppercase">TERMO DE QUITAÇÃO E OUTRAS AVENÇAS</h3>
                                    </div>

                                    <p className="text-justify">
                                        Pelo presente instrumento particular, de um lado, <strong>MODDER PRODUÇÕES ARTÍSTICAS</strong>, pessoa jurídica de direito privado, inscrita no CNPJ sob o nº 41.408.498/0001-03, com sede na Rua Itamar Branquinho, 56, Centro, Machado, MG, CEP 37750-000, neste ato representada por <strong>ALIFFE HENRIQUE DE CARVALHO</strong>, <strong>BRASILEIRO</strong>, <strong>SOLTEIRO</strong>, <strong>EMPRESÁRIO</strong>, residente no mesmo endereço, doravante denominada <strong>CONTRATANTE</strong>; e de outro lado, <strong>{business.businessName || '[EMPRESA MEI]'}</strong>, Microempreendedor Individual (MEI), inscrito no CNPJ sob o nº <strong>{business.cnpj || '[CNPJ]'}</strong>, com sede em <strong>{business.fullAddress || '[ENDEREÇO COMPLETO DO CONTRATADO]'}</strong>, doravante denominado <strong>CONTRATADO</strong>; têm entre si justo e acordado o quanto que segue:
                                    </p>

                                    <p className="text-justify">
                                        <strong>CONSIDERANDO</strong> que as Partes mantiveram relação contratual caracterizada pela prestação de serviços autônomos de apoio administrativo e suporte à comunidade, em <strong>{contract.startDate ? formatDate(contract.startDate) : (contract.contractSignedAt ? formatDate(contract.contractSignedAt) : '[DATA DE INÍCIO]')}</strong>, e que, de comum acordo, desejam formalizar e dar plena e recíproca quitação de todas as obrigações e direitos decorrentes dessa relação, pondo a termo também qualquer vínculo jurídico até aqui existente.
                                    </p>

                                    <div className="space-y-4">
                                        <h4 className="font-bold text-white uppercase">CLÁUSULA PRIMEIRA – DA QUITAÇÃO GERAL</h4>
                                        <p className="text-justify">
                                            1.1. As Partes, por este instrumento, declaram que todos os serviços contratados até a presente data foram devidamente prestados e integralmente pagos. Em razão disso, concedem-se mutuamente plena, geral, rasa, irrevogável e irretratável quitação de todas as obrigações e direitos, de qualquer natureza (fiscal, cível, trabalhista, previdenciária etc.), decorrentes da referida relação, não havendo mais nada a reclamar uma da outra, a qualquer tempo e a qualquer título, seja judicial ou extrajudicialmente.
                                        </p>
                                        <p className="text-justify">
                                            1.2. Independentemente da existência de instrumento contratual escrito anterior, as Partes neste ato ratificam que a relação profissional havida entre elas até a presente data foi exclusivamente de prestação de serviços autônomos, nos moldes estabelecidos neste termo.
                                        </p>
                                        <p className="text-justify">
                                            1.3. O CONTRATADO declara ter plena ciência e concordância com a natureza autônoma da prestação de serviços, tendo atuado com total autonomia e independência, sem qualquer tipo de controle de jornada, subordinação hierárquica ou dependência econômica em relação à CONTRATANTE.
                                        </p>
                                        <p className="text-justify">
                                            1.4. O CONTRATADO assume integral responsabilidade por todas as suas obrigações (fiscais, previdenciárias e trabalhistas, etc), decorrentes de sua atividade como Microempreendedor Individual (MEI), isentando a CONTRATANTE de qualquer responsabilidade nesse sentido.
                                        </p>
                                    </div>

                                    <div className="space-y-4">
                                        <h4 className="font-bold text-white uppercase">CLÁUSULA SEGUNDA – DA CONFIDENCIALIDADE</h4>
                                        <p className="text-justify">
                                            2.1. O CONTRATADO se obriga a manter sigilo absoluto sobre todas as informações confidenciais da CONTRATANTE a que teve acesso em razão da prestação de serviços, incluindo, mas não se limitando a, dados de clientes, estratégias de negócio, informações financeiras, processos internos e segredos comerciais. Esta obrigação de confidencialidade perdurará por tempo indeterminado, mesmo após a finalização da prestação de serviços.
                                        </p>
                                    </div>

                                    <div className="space-y-4">
                                        <h4 className="font-bold text-white uppercase">CLÁUSULA TERCEIRA – DO FORO</h4>
                                        <p className="text-justify">
                                            3.1. As Partes elegem o foro da comarca de Machado, Minas Gerais, para dirimir quaisquer dúvidas ou litígios decorrentes do presente termo, renunciando a qualquer outro, por mais privilegiado que seja.
                                        </p>
                                    </div>

                                    <p className="text-justify mt-8">
                                        E, por estarem justas e acordadas, as partes firmam o presente instrumento particular, em 02 (duas) vias de igual teor e forma, na presença das duas testemunhas abaixo identificadas.
                                    </p>

                                    <p className="text-justify mt-4 text-xs text-gray-500 italic">
                                        Termos enviado para o e-mail {user.email || '[EMAIL]'} para {personal.fullName || '[NOME]'} sob o CPF: {personal.cpf || '[CPF]'} representando o CNPJ: {business.cnpj || '[CNPJ]'}
                                    </p>

                                    <div className="text-center mt-8 mb-8">
                                        <p>Machado, MG, {formatDate(new Date())}</p>
                                    </div>

                                    <div className="grid grid-cols-1 gap-12 mt-8">
                                        <div>
                                            <div className="border-t border-white/30 w-3/4 mx-auto mb-2"></div>
                                            <p className="font-bold text-center">MODDER PRODUÇÕES ARTÍSTICAS</p>
                                            <p className="text-center text-xs">CONTRATANTE</p>
                                        </div>
                                        <div>
                                            <div className="border-t border-white/30 w-3/4 mx-auto mb-2"></div>
                                            <p className="font-bold text-center">{business.businessName || '[EMPRESA MEI]'}</p>
                                            <p className="text-center text-xs">CONTRATADO</p>
                                        </div>
                                    </div>

                                    <div className="mt-12 space-y-6">
                                        <p className="font-bold uppercase mb-4">TESTEMUNHAS:</p>
                                        <div>
                                            <div className="border-t border-white/30 w-1/2 mb-1"></div>
                                            <p className="text-sm">Nome: HIGOR ANTONIO BORGES DA SILVA RG: 705.963.694-33</p>
                                        </div>
                                        <div>
                                            <div className="border-t border-white/30 w-1/2 mb-1"></div>
                                            <p className="text-sm">Nome: Leonardo Biazon Freitas CPF: 035.050.830-54</p>
                                        </div>
                                    </div>

                                    <div className="mt-8 border-t border-gray-800 pt-4">
                                        <p className="font-bold text-xs text-gray-400">DADOS COMPLETOS DA INSCRIÇÃO DO PRESTADOR:</p>
                                        <div className="mt-2 text-xs text-gray-500 space-y-1">
                                            <p>Nome: {personal.fullName}</p>
                                            <p>CPF: {personal.cpf}</p>
                                            <p>Empresa: {business.businessName}</p>
                                            <p>CNPJ: {business.cnpj}</p>
                                            <p>Endereço: {business.fullAddress}</p>
                                        </div>
                                    </div>

                                    <div className="h-20"></div> {/* Extra space for scroll */}
                                </div>
                            </ScrollArea>

                            {/* Scroll Indicator */}
                            {!hasScrolled_bottom && (
                                <div className="text-center text-xs text-red-400 animate-pulse mt-4">
                                    Role até o fim para continuar
                                </div>
                            )}

                            {hasScrolled_bottom && (
                                <div className="mt-6 space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-gray-300">
                                            Assinatura Digital
                                        </label>
                                        <p className="text-xs text-gray-400">
                                            Digite seu nome completo exatamente como no cadastro para confirmar a leitura.
                                        </p>
                                        <input
                                            type="text"
                                            value={signature}
                                            onChange={(e) => setSignature(e.target.value)}
                                            placeholder={personal.fullName || "Seu Nome Completo"}
                                            className="w-full bg-gray-950 border border-gray-800 rounded-lg p-3 text-white focus:outline-none focus:border-red-500 transition-colors"
                                        />
                                    </div>

                                    <div className="flex justify-end pt-2">
                                        <Button
                                            className="bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-500/20"
                                            onClick={() => setStep(3)}
                                            disabled={!signature || signature.trim().toLowerCase() !== (personal.fullName || '').toLowerCase()}
                                        >
                                            Concordar e Prosseguir
                                            <ArrowRight className="ml-2 w-5 h-5" />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* STEP 3: NOTIFICATION & SIGNATURE */}
                    {step === 3 && (
                        <div className="text-center space-y-8 py-8">
                            <div className="w-20 h-20 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Mail className="w-10 h-10 text-blue-400" />
                            </div>

                            <h2 className="text-2xl font-bold text-white">Assinatura Digital</h2>

                            <div className="bg-blue-500/10 border border-blue-500/20 p-6 rounded-xl max-w-lg mx-auto text-left">
                                <h4 className="font-bold text-blue-400 flex items-center gap-2 mb-2">
                                    <AlertTriangle className="w-5 h-5" />
                                    Próximos Passos
                                </h4>
                                <p className="text-sm text-gray-300 text-justify">
                                    O <strong>Termo de Quitação</strong> oficial será enviado para o seu e-mail cadastrado (<strong>{user.email}</strong>) via DocuSign.
                                </p>
                                <p className="text-sm text-gray-300 mt-2 text-justify">
                                    Por favor, verifique sua caixa de entrada e spam. Após a assinatura eletrônica ser confirmada por você, o processo será finalizado.
                                </p>
                            </div>



                            <Button
                                className="w-full max-w-sm bg-red-600 hover:bg-red-700 text-white py-6 text-lg shadow-lg shadow-red-500/20"
                                onClick={() => onComplete(timestamps)}
                            >
                                <CheckCircle2 className="mr-2 w-5 h-5" />
                                JÁ ASSINEI NO E-MAIL
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TerminationOnboarding;
